import { Autocomplete, Typography } from '@mui/material';
import {
  forwardRef,
  ComponentType,
  memo,
  useCallback,
  useEffect,
  useRef,
  useContext,
  createContext,
  useMemo,
} from 'react';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import PmiTextField from '../text-field';
import debounce from 'shell/utils/debounce';
import { Consulate } from 'shell/models/consulate';
import { noop } from 'shell/utils/noop';
import { Operation } from 'shell/view-models/operation';

interface MunicipalityListProps {
  disabled?: boolean;
  value?: MunicipalityAnprVM | null;
  onChange: (municipality: MunicipalityAnprVM | undefined) => void;
  error?: boolean;
  label?: string;
  id?: string;
  istatCodeState?: string;
}

interface MunicipalityAnpr {
  flag: string;
  data: string;
  codConsolato: string;
  consolato: Consulate;
  comuneCessato: string;
  codTerritorio: string;
  provincia: string;
  regione: string;
  cap: number;
  key: {
    codice: string;
  };
  codIstat: string;
  nome: string;
  oldCode: string;
  tribunale: string;
  cdNazione: string;
}

export interface MunicipalityAnprVM {
  id?: string;
  description: string;
  province: string;
  provinceCode?: string;
  istatCode?: string;
  codConsulate?: string;
  nameConsulate?: string;
  item?: MunicipalityAnpr;
}

const MunicipalityListComponent = ({
  disabled,
  onChange,
  value,
  error,
  label = 'Comuni',
  id = 'municipality',
  istatCodeState,
}: MunicipalityListProps): React.ReactElement => {
  const [municipalities, getMunicipalities] = useHttpFetch<
    HttpResponseList<MunicipalityAnpr>,
    Array<MunicipalityAnprVM>
  >('', {
    onBootstrap: false,
    initialData: [],
    options: {
      loading: false,
      errorCatching: true,
      abortable: true,
    },
    transformer: (response: AxiosResponse<HttpResponseList<MunicipalityAnpr>>) => {
      const { data } = response;
      return data?.payload.list.map((item) => ({
        id: item.key.codice?.toString(),
        //description: `${item.nome} ${item.key.codice}`,
        description: item.nome,
        province: item.provincia,
        istatCode: item.codIstat,
        codConsulate: item.codConsolato,
        nameConsulate: item.consolato?.nome,
        item,
      })) as Array<MunicipalityAnprVM>;
    },
  });

  const handleSearch = debounce<string>(
    useCallback(
      (searchString) => {
        const { abort = noop } = municipalities;

        abort();

        getMunicipalities({
          url: `${__MUNICIPALITY_API_URL__}/geo/comune/stato-estero/${istatCodeState}`,
          params: {},
          paramsSerializer: () => `filters=nome:${searchString}:${Operation.LIKE}`,
        });
      },
      [municipalities.data, istatCodeState]
    )
  );

  const defaultProps = useMemo(
    () => ({
      options: (municipalities.data as Array<MunicipalityAnprVM>) || [],
      getOptionLabel: (option: MunicipalityAnprVM | null) => option?.description || '',
    }),
    [municipalities]
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      ListboxComponent={ListboxComponent}
      loading={municipalities.isLoading}
      disabled={disabled}
      value={value}
      onChange={(event, element) => {
        event.stopPropagation();
        onChange(element || undefined);
      }}
      onInputChange={(event, searchString) => {
        handleSearch(searchString);
      }}
      clearOnBlur={false}
      style={{ flex: 1 }}
      renderInput={(params) => (
        <PmiTextField {...params} label={label} variant="outlined" error={error} />
      )}
      renderOption={(props, option) => [props, option]}
    />
  );
};

function useResetCache(data: unknown) {
  const ref = useRef<VariableSizeList>(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const OuterElementContext = createContext({});

const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

OuterElementType.displayName = 'Outer element Type';

// Adapter for react-window
const ListboxComponent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  function ListboxComponent(props, ref) {
    // eslint-disable-next-line react/prop-types
    const { children, ...other } = props;
    const itemData: React.ReactChild[] = [];
    // eslint-disable-next-line react/prop-types
    (children as React.ReactChild[]).forEach(
      (item: React.ReactChild & { children?: React.ReactChild[] }) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
      }
    );

    const itemSize = 48;

    const itemCount = itemData.length;
    const gridRef = useResetCache(itemCount);

    const renderRow: ComponentType<ListChildComponentProps> = ({ data, index, style }) => {
      const dataSet = data[index];
      return (
        <Typography component="li" {...dataSet[0]} noWrap style={style}>
          {dataSet[1].description}
        </Typography>
      );
    };

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={6 * itemSize}
            width="100%"
            outerElementType={OuterElementType}
            ref={gridRef}
            innerElementType="ul"
            itemSize={() => 48}
            overscanCount={5}
            itemCount={itemCount}
          >
            {renderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  }
);

ListboxComponent.displayName = 'Virtualized list';

MunicipalityListComponent.displayName = 'Municipality list component';

const MunicipalityList = memo(MunicipalityListComponent);

export default MunicipalityList as typeof MunicipalityListComponent;
